import {
  Box,
  Chip,
  ChipDelete,
  Dropdown,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  Option,
  Select,
  Stack,
} from '@mui/joy';
import { Check, Plus, TrashSimple } from '@phosphor-icons/react';
import { ICellRendererParams } from 'ag-grid-community';
import { ColDef, ColGroupDef, RowClassRules } from 'ag-grid-community';
import { isEqual } from 'lodash';
import { ReactElement, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import partFinderNoFamilies from 'assets/partFinderNoFamilies.svg';
import {
  AttributeColorSwatch,
  getAttributeValueKey,
  getAttributeValueLabel,
} from 'companySettings/components/PartFinderAttributeValues';
import { AttributeType } from 'companySettings/enums';
import {
  IPartFinderFamilyAttribute,
  IPartFinderFamilyPart,
  IPartFinderFamilyPartValue,
} from 'companySettings/types';
import { AgGridTable, Button } from 'core/components';
import { DEFAULT_PLACEHOLDER } from 'core/constants';
import { AgGridFilterOption, TableLayoutType } from 'core/enums';
import {
  AgGridClientTableProvider,
  SelectionCountProvider,
  useAgGridClientTableContext,
  useFormContext,
} from 'core/states';
import { CopyValueCell, isButtonMouseEvent } from 'partFinder/components/CopyValueCell';
import { PartNumberCell } from 'partFinder/components/PartNumberCell';
import { Color, Direction, Size, Variant } from 'theme/enums';

import { AddPartNumberDrawer } from './AddPartNumberDrawer';
import { getAttributeUnits, getGroupKey, getUnitIndexes } from './groups';
import { IFamilyPartForm } from './types';
import {
  getAttributeKey,
  getPartKey,
  getRangeOptions,
  isPartValueChanged,
  isRangeAttribute,
} from './utils';
import { PartFinderEmptyState } from '../../PartFinderEmptyState';

const MAX_VALUE_CHIPS = 2;

const NO_BUTTONS: ReactElement[] = [];

// Columns can be made as narrow as the other Part Finder grids'.
const DEFAULT_COL_DEF = { minWidth: 40 };

type OnChangeValues = (
  partKey: string,
  attributeKey: string,
  values: IPartFinderFamilyPartValue[]
) => void;

interface IValueCellParams extends ICellRendererParams<IFamilyPartForm> {
  attribute: IPartFinderFamilyAttribute;
  onChangeValues: OnChangeValues;
}

// A part's values for one attribute: chips with ×, "+N" past the first two, and a menu of the
// attribute's values to switch one on or off. A Number & Range attribute takes one number.
const PartValueCell = ({ data, attribute, onChangeValues }: IValueCellParams) => {
  const { t } = useTranslation();

  if (!data) {
    return null;
  }

  const attributeKey = getAttributeKey(attribute);
  const partKey = getPartKey(data);
  const values = data.values?.[attributeKey] ?? [];

  if (isRangeAttribute(attribute)) {
    return (
      <Select
        size={Size.SM}
        placeholder={t('labelSelectValue')}
        value={values[0]?.numberValue ?? null}
        onChange={(_, numberValue) =>
          onChangeValues(
            partKey,
            attributeKey,
            numberValue ? [{ numberValue: String(numberValue) }] : []
          )
        }
      >
        {getRangeOptions(attribute.values).map((option) => (
          <Option key={option} value={option}>
            {option}
          </Option>
        ))}
      </Select>
    );
  }

  const valueKeys = new Set(values.map((value) => getAttributeValueKey(attribute.type, value)));
  const findValue = (value: IPartFinderFamilyPartValue) =>
    attribute.values.find(
      (attributeValue) =>
        getAttributeValueKey(attribute.type, attributeValue) ===
        getAttributeValueKey(attribute.type, value)
    );

  const toggle = (value: IPartFinderFamilyPartValue) => {
    const key = getAttributeValueKey(attribute.type, value);

    onChangeValues(
      partKey,
      attributeKey,
      valueKeys.has(key)
        ? values.filter((current) => getAttributeValueKey(attribute.type, current) !== key)
        : [...values, { value: value.value }]
    );
  };

  return (
    <Stack direction={Direction.ROW} sx={{ alignItems: 'center', gap: 0.5, height: '100%' }}>
      <Dropdown>
        <MenuButton
          endDecorator={null}
          slots={{ root: IconButton }}
          slotProps={{ root: { variant: Variant.PLAIN, color: Color.PRIMARY, size: Size.SM } }}
        >
          <Plus />
        </MenuButton>
        <Menu
          size={Size.SM}
          sx={{ zIndex: (theme) => theme.zIndex.modal + 1, maxHeight: 320, overflow: 'auto' }}
        >
          {attribute.values.map((attributeValue) => (
            // The name starts at the edge, after the swatch of a colour; the ✓ of a value the part
            // has is at the end.
            <MenuItem
              key={getAttributeValueKey(attribute.type, attributeValue)}
              onClick={() => toggle(attributeValue)}
              sx={{ gap: 1 }}
            >
              {AttributeType.COLOUR === attribute.type && (
                <AttributeColorSwatch colorHex={attributeValue.colorHex} />
              )}
              <Box sx={{ flexGrow: 1 }}>
                {getAttributeValueLabel(attribute.type, attributeValue, t)}
              </Box>
              {valueKeys.has(getAttributeValueKey(attribute.type, attributeValue)) && <Check />}
            </MenuItem>
          ))}
        </Menu>
      </Dropdown>

      {values.length === 0 && (
        <Chip size={Size.SM} variant={Variant.SOFT} color={Color.NEUTRAL}>
          {attribute.name}
        </Chip>
      )}

      {values.slice(0, MAX_VALUE_CHIPS).map((value) => (
        <Chip
          key={getAttributeValueKey(attribute.type, value)}
          size={Size.SM}
          variant={Variant.SOLID}
          color={Color.PRIMARY}
          startDecorator={
            AttributeType.COLOUR === attribute.type && (
              <AttributeColorSwatch colorHex={findValue(value)?.colorHex} />
            )
          }
          endDecorator={<ChipDelete onDelete={() => toggle(value)} />}
        >
          {value.value}
        </Chip>
      ))}

      {values.length > MAX_VALUE_CHIPS && (
        <Chip size={Size.SM} variant={Variant.SOFT} color={Color.NEUTRAL}>
          +{values.length - MAX_VALUE_CHIPS}
        </Chip>
      )}
    </Stack>
  );
};

const formatLocations = (locations: string | null) =>
  (locations ?? '')
    .split('|')
    .filter(Boolean)
    .map((location) => location.slice(location.indexOf(':') + 1))
    .join(', ');

// Every column has a fixed width and none flexes: the grid does not stretch them to its width,
// and a saved layout keeps exactly the widths it was saved with.
const getColumnDefs = (
  attributes: IPartFinderFamilyAttribute[],
  onChangeValues: OnChangeValues,
  t: (key: string) => string,
  loadedByKey?: Map<string, IFamilyPartForm>
): (ColDef<IFamilyPartForm> | ColGroupDef<IFamilyPartForm>)[] => [
  {
    colId: 'partNumber',
    width: 150,
    headerName: t('headerPartNumber'),
    field: 'partNumber',
    filter: 'agTextColumnFilter',
    cellRenderer: PartNumberCell,
    cellRendererParams: { suppressMouseEventHandling: isButtonMouseEvent },
  },
  {
    // The company's SKU for the part: the one paired with it, or the part's own number when that
    // is a SKU of the company's. A part the company does not carry has none.
    colId: 'sku',
    width: 150,
    headerName: t('headerSku'),
    field: 'sku',
    filter: 'agTextColumnFilter',
    valueFormatter: ({ value }) => value || DEFAULT_PLACEHOLDER,
    cellRenderer: CopyValueCell,
    cellRendererParams: {
      copyLabel: t('tooltipCopySku'),
      suppressMouseEventHandling: isButtonMouseEvent,
    },
  },
  {
    colId: 'description',
    width: 260,
    headerName: t('headerDescription'),
    field: 'description',
    filter: 'agTextColumnFilter',
  },
  {
    colId: 'locations',
    width: 140,
    headerName: t('headerLocation'),
    field: 'locations',
    filter: 'agTextColumnFilter',
    valueFormatter: ({ value }) => formatLocations(value),
  },
  {
    colId: 'manufacturerName',
    width: 160,
    headerName: t('headerManufacturer'),
    field: 'manufacturerName',
    filter: 'agTextColumnFilter',
  },
  // The attributes of a group sit under the group's name, as they do in Manage Attributes.
  ...getAttributeUnits(attributes).map(
    (unit): ColDef<IFamilyPartForm> | ColGroupDef<IFamilyPartForm> => {
      // A Number & Range attribute's part takes one number: its column filters ("at least" by
      // default) and sorts as a number. A Values or Colour one filters on the attribute's values.
      const toColumn = (attribute: IPartFinderFamilyAttribute): ColDef<IFamilyPartForm> => ({
        colId: `attribute-${getAttributeKey(attribute)}`,
        width: 160,
        headerName: attribute.name,
        // A saved part's value that is not saved yet.
        cellClassRules: {
          changedCell: ({ data }) => {
            const loaded = data && loadedByKey?.get(getPartKey(data));

            return Boolean(loaded) && isPartValueChanged(data, loaded, getAttributeKey(attribute));
          },
        },
        ...(isRangeAttribute(attribute)
          ? {
              valueGetter: ({ data }) => {
                const numberValue = data?.values?.[getAttributeKey(attribute)]?.[0]?.numberValue;

                return null === numberValue || undefined === numberValue || '' === numberValue
                  ? null
                  : Number(numberValue);
              },
              filter: 'agNumberColumnFilter',
              filterParams: { defaultOption: AgGridFilterOption.GREATER_THAN_OR_EQUAL },
            }
          : {
              // A part can have several of the attribute's values: the filter lists the
              // attribute's own values, and a part matches when one of its values is ticked. A
              // part without a value reads as blank, so the list's (Blanks) finds the parts still
              // to fill in.
              valueGetter: ({ data }) => {
                const values = data?.values?.[getAttributeKey(attribute)] ?? [];

                return values.length ? values.map((value) => value.value) : null;
              },
              filter: 'agSetColumnFilter',
              filterParams: { values: [...attribute.values.map((value) => value.value), null] },
              // Grouping would key on the list of values, not on one of them.
              enableRowGroup: false,
            }),
        cellRenderer: PartValueCell,
        cellRendererParams: { attribute, onChangeValues },
      });

      if ('group' === unit.type) {
        return {
          groupId: `group-${getGroupKey(unit.name)}`,
          headerName: unit.name,
          children: getUnitIndexes(unit).map((index) => toColumn(attributes[index])),
        };
      }

      return toColumn(attributes[unit.index]);
    }
  ),
];

interface IBulkProps {
  attributes: IPartFinderFamilyAttribute[];
  onApply: (partKeys: string[], attributeKey: string, values: IPartFinderFamilyPartValue[]) => void;
  onRemove: (partKeys: string[]) => void;
}

// With parts selected: set one attribute's value on all of them, or take them out of the family.
const BulkActions = ({ attributes, onApply, onRemove }: IBulkProps) => {
  const { t } = useTranslation();

  const { selectedRows } = useAgGridClientTableContext();

  const [attributeKey, setAttributeKey] = useState<string | null>(null);
  const [value, setValue] = useState<string | null>(null);

  const partKeys = ((selectedRows ?? []) as IFamilyPartForm[]).map(getPartKey);
  const attribute = attributes.find((current) => getAttributeKey(current) === attributeKey);
  const options = attribute
    ? isRangeAttribute(attribute)
      ? getRangeOptions(attribute.values)
      : attribute.values.map((attributeValue) => attributeValue.value)
    : [];

  return (
    <Stack direction={Direction.ROW} sx={{ alignItems: 'center', gap: 1 }}>
      <Select
        size={Size.SM}
        placeholder={t('labelAttribute')}
        value={attributeKey}
        onChange={(_, next) => {
          setAttributeKey(next);
          setValue(null);
        }}
        sx={{ minWidth: 160 }}
      >
        {attributes.map((current) => (
          <Option key={getAttributeKey(current)} value={getAttributeKey(current)}>
            {current.name}
          </Option>
        ))}
      </Select>
      <Select
        size={Size.SM}
        placeholder={t('labelSelectValue')}
        value={value}
        onChange={(_, next) => setValue(next)}
        disabled={!attribute}
        sx={{ minWidth: 160 }}
      >
        {options.map((option) => (
          <Option key={option} value={option}>
            {option}
          </Option>
        ))}
      </Select>
      <Button
        text={t('buttonLabelApply')}
        size={Size.SM}
        disabled={!attribute || !value}
        onClick={() =>
          onApply(partKeys, attributeKey, [
            isRangeAttribute(attribute) ? { numberValue: value } : { value },
          ])
        }
      />
      <Button
        text={t('buttonLabelRemove')}
        size={Size.SM}
        variant={Variant.SOFT}
        color={Color.DANGER}
        startDecorator={<TrashSimple />}
        onClick={() => onRemove(partKeys)}
      />
    </Stack>
  );
};

interface IProps {
  // The family editor's own buttons, which go in the grid's toolbar: before Add Part Numbers,
  // after it and on the right. With them the toolbar stays when the grid is empty, so the editor
  // can always be saved; the wizard, which has none, shows only the empty state.
  leadingButtons?: ReactElement[];
  trailingButtons?: ReactElement[];
  rightButtons?: ReactElement[];
  // The Add Part Number drawer's level: 1 over the wizard's panel, 0 on the editor's page.
  drawerLevel?: number;
  // The saved parts, in the editor: the rows and values that differ from them are highlighted.
  loadedParts?: IFamilyPartForm[];
}

// Step 3: the family's parts, one column per attribute the family uses, with their values. Parts
// are added one at a time from the part search; everything goes with the family's save.
export const PartsStep = ({
  leadingButtons = NO_BUTTONS,
  trailingButtons = NO_BUTTONS,
  rightButtons = NO_BUTTONS,
  drawerLevel = 1,
  loadedParts,
}: IProps) => {
  const { t } = useTranslation();

  const [isAddOpen, setIsAddOpen] = useState(false);

  const { watch, setValue } = useFormContext();

  const watchedParts: IFamilyPartForm[] | undefined = watch('parts');
  const watchedAttributes: IPartFinderFamilyAttribute[] | undefined = watch('attributes');
  const parts = useMemo(() => watchedParts ?? [], [watchedParts]);
  const attributes = useMemo(() => watchedAttributes ?? [], [watchedAttributes]);
  const usedAttributes = useMemo(
    () => attributes.filter((attribute) => attribute.inUse),
    [attributes]
  );

  const setParts = useCallback(
    (next: IFamilyPartForm[]) => setValue('parts', next, { shouldDirty: true }),
    [setValue]
  );

  const onChangeValues = useCallback<OnChangeValues>(
    (partKey, attributeKey, values) =>
      setParts(
        (watch('parts') ?? []).map((part: IFamilyPartForm) =>
          getPartKey(part) === partKey
            ? { ...part, values: { ...part.values, [attributeKey]: values } }
            : part
        )
      ),
    [setParts, watch]
  );

  const onAdded = (part: IPartFinderFamilyPart) => {
    if (parts.some((current) => getPartKey(current) === getPartKey(part))) {
      return false;
    }

    setParts([...parts, { ...part, values: {} }]);

    return true;
  };

  // The grid names a row by its part: the id a saved part has is the server's, not the grid's.
  const rows = useMemo(() => parts.map((part) => ({ ...part, id: getPartKey(part) })), [parts]);

  const loadedByKey = useMemo(
    () => loadedParts && new Map(loadedParts.map((part) => [getPartKey(part), part])),
    [loadedParts]
  );

  const columnDefs = useMemo(
    () => getColumnDefs(usedAttributes, onChangeValues, t, loadedByKey),
    [loadedByKey, onChangeValues, t, usedAttributes]
  );

  // A new part, or a saved one with other values, until the family is saved.
  const rowClassRules = useMemo<RowClassRules<IFamilyPartForm>>(
    () =>
      loadedByKey
        ? {
            addedRow: ({ data }) => Boolean(data) && !loadedByKey.has(getPartKey(data)),
            changedRow: ({ data }) => {
              const loaded = data && loadedByKey.get(getPartKey(data));

              return Boolean(loaded) && !isEqual(data.values ?? {}, loaded.values ?? {});
            },
          }
        : undefined,
    [loadedByKey]
  );

  const actionButtons = useCallback(
    () => [
      ...leadingButtons,
      <Button
        key="addPartNumbersButton"
        text={t('buttonLabelAddPartNumbers')}
        startDecorator={<Plus />}
        onClick={() => setIsAddOpen(true)}
      />,
      ...trailingButtons,
    ],
    [leadingButtons, t, trailingButtons]
  );

  const hasOwnButtons =
    leadingButtons.length > 0 || trailingButtons.length > 0 || rightButtons.length > 0;

  const selectionActionButtons = useMemo(
    () => [
      <BulkActions
        key="bulkActions"
        attributes={usedAttributes}
        onApply={(partKeys, attributeKey, values) =>
          setParts(
            parts.map((part) =>
              partKeys.includes(getPartKey(part))
                ? { ...part, values: { ...part.values, [attributeKey]: values } }
                : part
            )
          )
        }
        onRemove={(partKeys) =>
          setParts(parts.filter((part) => !partKeys.includes(getPartKey(part))))
        }
      />,
    ],
    [parts, setParts, usedAttributes]
  );

  const emptyStateElement = useMemo(
    () => (
      <PartFinderEmptyState
        image={partFinderNoFamilies}
        message={t('messagePartFinderFamilyNoParts')}
        action={
          <Button
            text={t('buttonLabelAddPartNumbers')}
            color={Color.SUCCESS}
            size={Size.MD}
            onClick={() => setIsAddOpen(true)}
          />
        }
      />
    ),
    [t]
  );

  return (
    <>
      <SelectionCountProvider>
        <AgGridClientTableProvider
          tableId="partFinderFamilyParts"
          entityType="FamilyPart"
          columnDefs={columnDefs}
          data={rows}
          rowClassRules={rowClassRules}
          // The rows are the form's: there is nothing to load.
          isDataLoading={false}
          loadingPercentage={100}
          dataCurrentPage={1}
          layoutType={TableLayoutType.PART_FINDER_FAMILY_PART}
          defaultColDef={DEFAULT_COL_DEF}
          hasLayoutActions
          headerCheckbox
        >
          <AgGridTable
            actionButtons={actionButtons}
            rightActionButtons={rightButtons}
            selectionActionButtons={selectionActionButtons}
            emptyStateElement={emptyStateElement}
            hideTopActionsWhenEmpty={!hasOwnButtons}
          />
        </AgGridClientTableProvider>
      </SelectionCountProvider>

      <AddPartNumberDrawer
        isOpen={isAddOpen}
        onClose={() => setIsAddOpen(false)}
        onAdded={onAdded}
        level={drawerLevel}
      />
    </>
  );
};
